import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { t } from 'i18next';
import { useTable, useGlobalFilter, useSortBy } from 'react-table';
import SitecorecontextFactory from '../../../lib/SitecoreContextFactory';
import Search from '../../../assets/icons/Search.svg';
import ChevronSmallDown from '../../../assets/icons/ChevronSmallDown.svg';
import ChevronSmallDownOrange from '../../../assets/icons/ChevronSmallDownOrange.svg';
import PaginationStart from '../../../assets/icons/PaginationStart.svg';
import PaginationStartDisabled from '../../../assets/icons/PaginationStartDisabled.svg';
import PaginationEnd from '../../../assets/icons/PaginationEnd.svg';
import PaginationEndDisabled from '../../../assets/icons/PaginationEndDisabled.svg';
import Backward from '../../../assets/icons/Backward.svg';
import BackwardDisabled from '../../../assets/icons/BackwardDisabled.svg';
import Forward from '../../../assets/icons/Forward.svg';
import ForwardDisabled from '../../../assets/icons/ForwardDisabled.svg';
import Download from '../../../assets/icons/Download.svg';
import Filter from '../../../assets/icons/Filter16x16.svg';
import TextInput from '../../../lib/ui/form_elements/TextInput';
import TertiaryButton from '../../../lib/ui/buttons/TertiaryButton';
import { Locales } from '../../../utils/enums';
import {
  FiltersAndTableContainer,
  TableContainer,
  StyledTable,
  TableDataCell,
  ReversedIcon,
  CellContainer,
  PaginationContainer,
  PageNumber,
  NoResults,
} from './css';
import {
  FiltersContainer,
  MobileActionsContainer,
  ActionsContainer,
} from '../css';
import { DropdownActions } from '../index';
import { SORTING_DIRECTION } from '../utils';

const Table = ({
  data,
  columns,
  handleDownloadReportClick,
  setIsCustomizeMenuOpen,
  setIsFiltersMenuOpen,
  pageNumber,
  onPageChange,
  totalPages,
  numberOfFilters,
  handleSearch,
  changeFieldOrder,
  fieldOrder,
  activeTab,
}) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state: { globalFilter },
  } = useTable({ columns, data }, useGlobalFilter, useSortBy);

  const [globalFilterValue, setGlobalFilterValue] = useState(globalFilter);

  const getPaginationRange = () => {
    // Constants...
    const START_PAGE = 1;
    const DEFAULT_RANGE = 5;

    // Initializations...
    let range = DEFAULT_RANGE;

    if (range > totalPages) {
      range = totalPages;
    }

    let start = pageNumber - Math.floor(range / 2);
    start = Math.max(start, START_PAGE);
    start = Math.min(start, START_PAGE + totalPages - range);

    return Array.from(
      { length: range },
      (element, index) => start + index,
    );
  };

  return (
    <FiltersAndTableContainer>
      <FiltersContainer>
        {(SitecorecontextFactory.context.language === Locales.English) && (
          <TextInput
            placeholder="Search"
            icon={{ src: Search, alt: 'Search', onClick: () => handleSearch(globalFilterValue) }}
            input={{
              value: (globalFilterValue || ''),
              onChange: (event) => setGlobalFilterValue(event.target.value),
              onKeyDown: (event) => {
                if (event.charCode === 13 || event.keyCode === 13) {
                  event.preventDefault();
                  event.stopPropagation();
                  handleSearch(globalFilterValue);
                }
              },
            }}
          />
        )}
        {(SitecorecontextFactory.context.language === Locales.French) && (
        <TextInput
          placeholder="Rechercher"
          icon={{ src: Search, alt: 'Rechercher', onClick: () => handleSearch(globalFilterValue) }}
          input={{
            value: (globalFilterValue || ''),
            onChange: (event) => setGlobalFilterValue(event.target.value),
            onKeyDown: (event) => {
              if (event.charCode === 13 || event.keyCode === 13) {
                event.preventDefault();
                event.stopPropagation();
                handleSearch(globalFilterValue);
              }
            },
          }}
        />

        )}

        <ActionsContainer>
          <TertiaryButton
            label={t('DownloadReport')}
            onClick={(e) => handleDownloadReportClick(e)}
            size="small"
            showArrow={false}
            iconSrc={Download}
            isIconLeftPosition
          />
          <TertiaryButton
            label={`${t('Filters')}${numberOfFilters ? (` (${numberOfFilters})`) : ''}`}
            onClick={() => setIsFiltersMenuOpen(true)}
            size="small"
            showArrow={false}
            iconSrc={Filter}
            isIconLeftPosition
          />
        </ActionsContainer>

        <MobileActionsContainer>
          <DropdownActions
            handleDownloadReportClick={handleDownloadReportClick}
            toggleCustomizeMenu={() => setIsCustomizeMenuOpen(true)}
            toggleFilterMenu={() => setIsFiltersMenuOpen(true)}
          />
        </MobileActionsContainer>
      </FiltersContainer>

      <TableContainer>
        {!(rows && rows.length) && (
        <NoResults>
          {t('NoResults')}
        </NoResults>
        )}

        {(data && data.length && totalPages > 1) ? (
          <PaginationContainer>
            <TertiaryButton
              label={t('First')}
              onClick={() => onPageChange(1)}
              size="small"
              showArrow={false}
              iconSrc={PaginationStart}
              iconSrcDisabled={PaginationStartDisabled}
              isIconLeftPosition
              disabled={pageNumber === 1}
            />
            <TertiaryButton
              label={t('Previous')}
              onClick={() => onPageChange(pageNumber - 1)}
              size="small"
              showArrow={false}
              iconSrc={Backward}
              iconSrcDisabled={BackwardDisabled}
              isIconLeftPosition
              disabled={pageNumber === 1}
            />
            {totalPages && pageNumber && getPaginationRange().map((page) => (
              <PageNumber
                key={page}
                selected={(pageNumber === page)}
                onClick={() => onPageChange(page)}
              >
                {page}
              </PageNumber>
            ))}
            <TertiaryButton
              label={t('Next')}
              onClick={() => onPageChange(pageNumber + 1)}
              size="small"
              showArrow={false}
              iconSrc={Forward}
              iconSrcDisabled={ForwardDisabled}
              disabled={pageNumber === totalPages}
            />
            <TertiaryButton
              label={t('Last')}
              onClick={() => onPageChange(totalPages)}
              size="small"
              showArrow={false}
              iconSrc={PaginationEnd}
              iconSrcDisabled={PaginationEndDisabled}
              disabled={pageNumber === totalPages}
            />
          </PaginationContainer>
        ) : null}

        <StyledTable {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup, index) => (
              <tr {...headerGroup.getHeaderGroupProps()} key={index}>
                {headerGroup.headers.map((column, idx) => (
                  <th
                    key={column.id}
                    className={(idx < 2) ? `sticky table-header column-${idx}` : ''}
                    onClick={() => changeFieldOrder(column.name)}
                    {...column.getHeaderProps()}
                  >
                    {column.label}
                    {(column.canSort && column.isSorted)
                      ? null
                      : (fieldOrder[activeTab] && column.name === fieldOrder[activeTab].fieldName
                              && fieldOrder[activeTab].direction === SORTING_DIRECTION.DESC)
                        ? <img src={ChevronSmallDownOrange} alt="sort descending" />
                        : (fieldOrder[activeTab] && column.name === fieldOrder[activeTab].fieldName
                                && fieldOrder[activeTab].direction === SORTING_DIRECTION.ASC)
                          ? <ReversedIcon src={ChevronSmallDownOrange} alt="sort ascending" />
                          : <img src={ChevronSmallDown} alt="sort" />}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody {...getTableBodyProps()}>
            {rows.map((row, index) => {
              prepareRow(row);

              return (
                <tr {...row.getRowProps()} key={index}>
                  {row.cells.map((cell, idx) => {
                    let cellElement = cell.render('Cell');

                    if (cell.column && (cell.column.name === 'shipper' || cell.column.name === 'consignee')) {
                      cellElement = (

                        <CellContainer>
                          <span>{cell.row.original[`${cell.column.name}Name`]}</span>
                          <div>
                            <div>
                              {cell.row.original[`${cell.column.name}Address`]}
                              ,
                            </div>
                            <div>
                              {cell.row.original[`${cell.column.name}City`]
                                ? `${cell.row.original[`${cell.column.name}City`]} ,`
                                : ''}
                              {' '}
                              {cell.row.original[`${cell.column.name}State`]}
                            </div>
                            <div>{cell.row.original[`${cell.column.name}ZipCode`]}</div>
                          </div>
                        </CellContainer>
                      );
                    }

                    return (
                      <TableDataCell
                        key={index}
                        className={(idx < 2) ? `sticky table-cell column-${idx}` : ''}
                        topAligned={cell.column.name === 'status'}
                        textAlignCenter={cell.column.name === 'actions'}
                        {...cell.getCellProps()}
                      >
                        {cellElement}
                      </TableDataCell>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </StyledTable>
      </TableContainer>
    </FiltersAndTableContainer>
  );
};

Table.propTypes = {
  data: PropTypes.array,
  columns: PropTypes.array,
  handleDownloadReportClick: PropTypes.func,
  setIsCustomizeMenuOpen: PropTypes.func,
  setIsFiltersMenuOpen: PropTypes.func,
  pageNumber: PropTypes.number,
  onPageChange: PropTypes.func,
  totalPages: PropTypes.number,
  numberOfFilters: PropTypes.number,
  handleSearch: PropTypes.func,
  changeFieldOrder: PropTypes.func,
  fieldOrder: PropTypes.object,
  activeTab: PropTypes.string,
  changeActiveTab: PropTypes.func,
};

export default Table;
